import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { Box, Typography, CircularProgress, Alert, Button } from '@mui/material';
import { getPayPalConfig, createPayPalOrder, capturePayPalPayment, CapturePayPalPaymentResponse } from '../../services/paymentService';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';

interface StandardPaymentComponentProps {
  sessionIds: number[];
  discountCode?: string;
  onPaymentComplete: (data: CapturePayPalPaymentResponse) => void;
  onPaymentError: (error: string) => void;
}

const StandardPaymentComponent: React.FC<StandardPaymentComponentProps> = ({
  sessionIds,
  discountCode,
  onPaymentComplete,
  onPaymentError
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [paypalConfig, setPaypalConfig] = useState<{ clientId: string; mode: string } | null>(null);

  // Terminal capture failure: a capture error where money may have moved must
  // NOT silently re-enable the button and invite an immediate retry.
  const [captureFailed, setCaptureFailed] = useState(false);
  // Benign terminal outcome: the cart was already enrolled, so no charge happened.
  const [alreadyEnrolled, setAlreadyEnrolled] = useState(false);

  // In-flight / cached createOrder guard. Ensures at most ONE backend
  // create-order call per cart attempt even if PayPal invokes createOrder
  // multiple times in rapid succession.
  const createOrderPromiseRef = useRef<Promise<string> | null>(null);

  // Payment id is stored in a ref (not state) because the PayPal SDK controls
  // when onApprove fires relative to React re-renders. A state value could
  // still be null in the closure onApprove runs in, so we set this
  // synchronously in createOrder and read it back in onApprove.
  const paymentIdRef = useRef<string | null>(null);
  
  // Load the PayPal configuration
  useEffect(() => {
    const loadPayPalConfig = async () => {
      try {
        setLoading(true);
        
        // Get the configuration from our backend
        const config = await getPayPalConfig();
        setPaypalConfig(config);
        
        setLoading(false);
      } catch (err: unknown) {
        console.error('Error setting up PayPal:', err);
        const error = err as { message?: string };
        setError(`Failed to set up payment system: ${error?.message || 'Unknown error'}`);
        setLoading(false);
      }
    };
    
    loadPayPalConfig();
  }, []);
  
  // Reset the in-flight create-order guard whenever the cart changes so a new
  // cart starts a fresh single create. Keyed on the same inputs PayPalButtons
  // uses for forceReRender.
  useEffect(() => {
    createOrderPromiseRef.current = null;
  }, [sessionIds, discountCode]);

  // Standard PayPal integration - create order with useCallback for memoization.
  // In-flight guard: if a create is already pending or resolved for the current
  // cart, return the existing promise instead of issuing a second backend call.
  const createOrder = useCallback(async () => {
    if (createOrderPromiseRef.current) {
      return createOrderPromiseRef.current;
    }

    const promise = (async () => {
      try {
        // Create the order using our backend endpoint
        const orderData = await createPayPalOrder(sessionIds, discountCode);

        // Store the payment ID synchronously in a ref so onApprove always reads
        // the correct value regardless of React re-render timing.
        paymentIdRef.current = orderData.paymentId;
        console.log('Created PayPal order:', orderData.orderId, 'with payment ID:', orderData.paymentId);

        return orderData.orderId;
      } catch (err: unknown) {
        // Allow a retry of order creation after a failure.
        createOrderPromiseRef.current = null;
        console.error("Error creating order:", err);
        const error = err as { message?: string };
        onPaymentError(`Failed to create payment: ${error?.message || 'Unknown error'}`);
        throw new Error('Failed to create order');
      }
    })();

    createOrderPromiseRef.current = promise;
    return promise;
  }, [sessionIds, discountCode, onPaymentError]);

  // Handle approval with useCallback for memoization
  const onApprove = useCallback(async (data: { orderID: string }) => {
    setProcessing(true);

    try {
      const paymentId = paymentIdRef.current;
      if (!paymentId) {
        throw new Error('Payment ID is missing');
      }

      console.log(`Capturing PayPal payment. OrderID: ${data.orderID}, PaymentId: ${paymentId}`);

      // Capture the payment using our backend endpoint with the correct payment ID
      const result = await capturePayPalPayment(data.orderID, paymentId);

      console.log('Payment captured:', result);

      // Backend signalled the cart was already enrolled: no charge happened.
      // Treat as a benign terminal outcome (not success, not error).
      if (result.blocked) {
        setAlreadyEnrolled(true);
        return;
      }

      onPaymentComplete(result);
    } catch (err: unknown) {
      console.error('Error processing payment:', err);
      const error = err as { message?: string };
      // Terminal failure: money may have moved. Keep the button disabled and
      // do NOT re-enable in finally, so the user cannot immediately retry.
      setCaptureFailed(true);
      onPaymentError(`Payment processing failed: ${error?.message || 'Unknown error'}`);
    } finally {
      setProcessing(false);
    }
  }, [onPaymentComplete, onPaymentError]);
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }
  
  // Only render when we have a client ID
  if (!paypalConfig?.clientId) {
    return (
      <Alert severity="warning">
        PayPal configuration is loading. Please wait...
      </Alert>
    );
  }

  // Benign terminal outcome: cart already enrolled, no charge made.
  if (alreadyEnrolled) {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          These enrollments already exist; no charge was made.
        </Alert>
        <Button
          variant="contained"
          href="/dashboard"
          data-testid="blocked-dashboard-button"
        >
          Go to Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Payment Information
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        Complete your payment using PayPal or credit/debit card.
      </Typography>

      {captureFailed && (
        <Alert severity="error" sx={{ mb: 3 }} data-testid="capture-terminal-error">
          Payment could not be completed — do not retry. Please check your
          dashboard or contact support to confirm the status of your payment.
        </Alert>
      )}

      {/* Standard PayPal integration */}
      <PayPalScriptProvider
        options={{
          clientId: paypalConfig.clientId,
          currency: 'USD',
          intent: 'capture',
        }}
      >
        <Box sx={{ minHeight: 200, mb: 3 }}>
          <PayPalButtons
            style={{
              layout: "vertical",
              shape: "rect",
              color: "gold"
            }}
            disabled={processing || captureFailed}
            forceReRender={[paypalConfig.clientId, JSON.stringify(sessionIds), discountCode]}
            createOrder={createOrder}
            onApprove={onApprove}
            onError={(err: unknown) => {
              console.error("PayPal Buttons error:", err);
              onPaymentError("There was an error with the payment process. Please try again.");
            }}
            onCancel={() => {
              console.log("Payment cancelled");
              onPaymentError("Payment was cancelled. Please try again when you're ready.");
            }}
          />
        </Box>
      </PayPalScriptProvider>
      
      {processing && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Processing payment...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default memo(StandardPaymentComponent);